import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NavigationContractItem, RouteId } from '@finance/contracts';
import { useBackendNavigation } from '../../runtime/navigation/use-navigation';
import { getRouteById } from '../../routes/registry';

function isActionable(item: NavigationContractItem): boolean {
  return item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY';
}

export interface UseShellHeaderResult {
  items: NavigationContractItem[];
  activeRouteId: RouteId | false;
  navigationLoading: boolean;
  navigationError: boolean;
  refetchNavigation: () => Promise<unknown>;
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (value: boolean) => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (value: boolean) => void;
  dashboardPath: string;
  primaryNavigationLabel: string;
}

export function useShellHeader(): UseShellHeaderResult {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: contract, isLoading: navigationLoading, isError: navigationError, refetch: refetchNavigation } = useBackendNavigation();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const dashboardPath = getRouteById('dashboard' as RouteId)?.path ?? '/dashboard';
  const primaryNavigationLabel = t('shell:primaryNavigationLabel');

  const items = useMemo(() => {
    return (contract?.primary ?? []).filter((item): item is NavigationContractItem => item.availability !== 'HIDDEN');
  }, [contract]);

  const activeRouteId = useMemo(() => {
    const activeItem = items.find((item) => {
      const route = getRouteById(item.routeId);
      return Boolean(route && (location.pathname === route.path || location.pathname.startsWith(`${route.path}/`)));
    });
    return activeItem?.routeId ?? false;
  }, [items, location.pathname]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k')) return;
      if (commandPaletteOpen) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) return;
      event.preventDefault();
      setCommandPaletteOpen(true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen]);

  return {
    items,
    activeRouteId,
    navigationLoading,
    navigationError,
    refetchNavigation,
    commandPaletteOpen,
    setCommandPaletteOpen,
    mobileNavOpen,
    setMobileNavOpen,
    dashboardPath,
    primaryNavigationLabel,
  };
}
